import { t as tr } from './i18n.js'
import { useEffect, useMemo, useRef, useState } from 'react'
import { ArrowDown, ArrowUp } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { api, type Portfolio, type Project, type SearchResults } from './api.js'
import { atLeast, useAuth } from './auth.js'
import { useTimer } from './timer.js'
import { usePref } from './prefs.js'

interface Cmd { id: string; label: string; hint?: string; run: () => void; group: string }

/** Command palette: Ctrl-K (Cmd-K on Mac). Navigation, projects, actions, and live search. */
export function CommandPalette() {
  const nav = useNavigate()
  const { user, signOut } = useAuth()
  const timer = useTimer()
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [idx, setIdx] = useState(0)
  const input = useRef<HTMLInputElement>(null)
  const projects = useQuery({ queryKey: ['projects', false], queryFn: () => api<{ projects: Project[] }>('/projects'), enabled: open })
  const portfolios = useQuery({ queryKey: ['portfolios'], queryFn: () => api<{ portfolios: Portfolio[] }>('/portfolios'), enabled: open })
  const [hidden] = usePref<string[]>('hiddenProjects', [])
  const search = useQuery({ queryKey: ['search', q], queryFn: () => api<SearchResults>(`/search?q=${encodeURIComponent(q)}&limit=8`), enabled: open && q.trim().length >= 2 })

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setOpen((o) => !o); setQ(''); setIdx(0) }
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])
  useEffect(() => { if (open) setTimeout(() => input.current?.focus(), 0) }, [open])

  const go = (to: string) => { setOpen(false); nav(to) }
  const cmds = useMemo<Cmd[]>(() => {
    const base: Cmd[] = [
      { id: 'home', label: 'Go to Home', run: () => go('/'), group: 'Navigate' },
      { id: 'time', label: 'Go to Time', hint: 'log hours', run: () => go('/time'), group: 'Navigate' },
      { id: 'timesheets', label: 'Go to Timesheets', run: () => go('/timesheets'), group: 'Navigate' },
      { id: 'leave', label: 'Go to Leave', run: () => go('/leave'), group: 'Navigate' },
      { id: 'reports', label: 'Go to Reports', run: () => go('/reports'), group: 'Navigate' },
      { id: 'projects', label: 'Go to Projects', run: () => go('/projects'), group: 'Navigate' },
      { id: 'portfolios', label: 'Go to Portfolios', run: () => go('/portfolios'), group: 'Navigate' },
      { id: 'people', label: 'Go to People', run: () => go('/people'), group: 'Navigate' },
      { id: 'account', label: 'Account & security', run: () => go('/account'), group: 'Navigate' },
      ...(atLeast(user, 'manager') ? [{ id: 'payroll', label: 'Go to Payroll', run: () => go('/payroll'), group: 'Navigate' }, { id: 'newproj', label: 'New project', run: () => go('/projects/new'), group: 'Actions' }] : []),
      ...(atLeast(user, 'admin') ? [{ id: 'admin', label: 'Admin console', run: () => go('/admin'), group: 'Navigate' }, { id: 'import', label: 'Import CSV', run: () => go('/admin/import'), group: 'Actions' }] : []),
      ...(timer.timer ? [{ id: 'stoptimer', label: `Stop timer (${timer.timer.projectCode})`, run: () => { setOpen(false); void timer.stop(timer.timer!.projectId) }, group: 'Actions' }] : []),
      { id: 'signout', label: 'Sign out', run: () => { setOpen(false); void signOut() }, group: 'Actions' },
    ]
    const projs: Cmd[] = (projects.data?.projects ?? []).filter((p) => !hidden.includes(p.id)).flatMap((p) => [
      { id: `p-${p.id}`, label: `${p.code} ${p.name}`, hint: 'project', run: () => go(`/projects/${p.id}`), group: 'Projects' },
      { id: `pb-${p.id}`, label: `${p.code} board`, run: () => go(`/projects/${p.id}/board`), group: 'Projects' },
    ])
    const pfs: Cmd[] = (portfolios.data?.portfolios ?? []).filter((p) => !p.archivedAt).flatMap((p) => [
      { id: `pf-${p.id}`, label: p.name, hint: 'portfolio', run: () => go(`/portfolios/${p.id}`), group: 'Portfolios' },
      { id: `pfb-${p.id}`, label: `${p.name} board`, run: () => go(`/portfolios/${p.id}/board`), group: 'Portfolios' },
    ])
    const found: Cmd[] = [
      ...(search.data?.tasks ?? []).map((t) => ({ id: `t-${t.id}`, label: `${t.key} ${t.title}`, hint: t.statusName, run: () => go(`/projects/${t.projectId}?task=${t.id}`), group: 'Tasks' })),
      ...(search.data?.people ?? []).map((u) => ({ id: `u-${u.id}`, label: u.displayName, hint: u.email, run: () => go('/people'), group: 'People' })),
    ]
    // One block per group, in a fixed order, sorted alphabetically inside each block.
    const ORDER = ['Navigate', 'Actions', 'Projects', 'Portfolios', 'Tasks', 'People']
    const all = [...base, ...projs, ...pfs, ...found].sort((a, b) => (ORDER.indexOf(a.group) - ORDER.indexOf(b.group)) || a.label.localeCompare(b.label))
    const needle = q.trim().toLowerCase()
    return needle ? all.filter((c) => c.label.toLowerCase().includes(needle) || (c.hint ?? '').toLowerCase().includes(needle)) : all.filter((c) => c.group === 'Navigate' || c.group === 'Actions')
  }, [q, projects.data, portfolios.data, search.data, user, timer.timer, hidden]) // go/signOut/timer.stop are stable enough for a palette

  if (!open) return null
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setIdx((i) => Math.min(cmds.length - 1, i + 1)) }
    if (e.key === 'ArrowUp') { e.preventDefault(); setIdx((i) => Math.max(0, i - 1)) }
    if (e.key === 'Enter') { e.preventDefault(); cmds[idx]?.run() }
  }
  let lastGroup = ''
  return (
    <div className="palette-backdrop" onClick={() => setOpen(false)}>
      <div className="palette" role="dialog" aria-label={tr("Command palette")} onClick={(e) => e.stopPropagation()}>
        <input ref={input} value={q} onChange={(e) => { setQ(e.target.value); setIdx(0) }} onKeyDown={onKey} placeholder={tr("Type a command, project, task key, or name…  (Ctrl-K toggles)")} aria-label={tr("Command")} />
        <ul>
          {cmds.slice(0, 40).map((c, i) => {
            const head = c.group !== lastGroup ? c.group : null; lastGroup = c.group
            return (<li key={c.id}>{head && <div className="pgroup">{head}</div>}<button className={i === idx ? 'active' : ''} onMouseEnter={() => setIdx(i)} onClick={c.run}>{c.label}{c.hint && <span className="muted small"> · {c.hint}</span>}</button></li>)
          })}
          {cmds.length === 0 && <li className="muted small" style={{ padding: '.5rem .75rem' }}>No matches{search.isFetching ? '…' : ''}</li>}
        </ul>
        <div className="small muted phint"><ArrowUp size={12} aria-hidden /><ArrowDown size={12} aria-hidden /> {tr("navigate · Enter run · Esc close")}</div>
      </div>
    </div>
  )
}
